import type { ConversationEventListener } from "./marketplaceApi";
import type {
  CatalogLocationFilter,
  ChatAttachment,
  AdComment,  ChatInbox,
  ChatInboxItem,
  ChatMessage,
  CityLocation,
  Conversation,
  Draft,
  Listing,
  PreviewState,
  RegionLocation,
  SellerProfile,
  SellerReview,
} from "./types";
import { emptyDraft, seedListings } from "./types";

const STORAGE_KEY = "freevito.preview.state.v1";

const previewRegions: RegionLocation[] = [
  { code: "GN-73", name: "Республика Татарстан", type: "респ", federalDistrict: "Приволжский" },
  { code: "GN-48", name: "Москва", type: "г", federalDistrict: "Центральный" },
  { code: "GN-66", name: "Санкт-Петербург", type: "г", federalDistrict: "Северо-Западный" },
];

const previewCities: CityLocation[] = [
  {
    id: "551487",
    name: "Казань",
    region: { code: "GN-73", name: "Республика Татарстан" },
    isRegionCapital: true,
  },
  {
    id: "524901",
    name: "Москва",
    region: { code: "GN-48", name: "Москва" },
    isRegionCapital: true,
  },
  {
    id: "498817",
    name: "Санкт-Петербург",
    region: { code: "GN-66", name: "Санкт-Петербург" },
    isRegionCapital: true,
  },
];

function normalizeCitySearch(value: string): string {
  return value.trim().toLocaleLowerCase("ru-RU").replaceAll("ё", "е");
}

function readState(): PreviewState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const saved = JSON.parse(raw) as Partial<PreviewState>;
      return {
        ...defaultState(),
        ...saved,
        draft: { ...emptyDraft(), ...saved.draft },
      };
    }
  } catch {
    // Preview remains usable when storage is blocked.
  }
  return defaultState();
}

function defaultState(): PreviewState {
  return {
    favorites: [],
    messages: {},
    draft: emptyDraft(),
    submitted: false,
    offline: false,
  };
}

function saveState(state: PreviewState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // UI state still works for the current session.
  }
}

export class MockApi {
  private state = readState();
  private privateAttachmentUrls = new Map<string, string[]>();

  get offline(): boolean {
    return this.state.offline;
  }

  setOffline(value: boolean): void {
    this.state.offline = value;
    saveState(this.state);
  }

  private async request<T>(value: T): Promise<T> {
    await new Promise((resolve) => window.setTimeout(resolve, 90));
    if (this.state.offline) throw new Error("offline");
    return value;
  }

  async getListings(category = "Всё", location: CatalogLocationFilter = {}): Promise<Listing[]> {
    const listings = seedListings
      .filter((item) => category === "Всё" || item.category === category)
      .filter((item) => !location.regionCode || item.regionCode === location.regionCode)
      .filter((item) => !location.cityId || item.cityId === location.cityId)
      .map((item) => ({ ...item, favorite: this.state.favorites.includes(item.id) }));
    return this.request(listings);
  }

  async getRegions(): Promise<RegionLocation[]> {
    return this.request(previewRegions.map((region) => ({ ...region })));
  }

  async searchCities(regionCode: string, query: string): Promise<CityLocation[]> {
    const normalizedQuery = normalizeCitySearch(query);
    if (normalizedQuery.length < 2) return this.request([]);
    const matches = previewCities.filter(
      (city) => city.region.code === regionCode && normalizeCitySearch(city.name).startsWith(normalizedQuery),
    );
    return this.request(matches.map((city) => ({ ...city, region: { ...city.region } })));
  }

  async toggleFavorite(id: string): Promise<boolean> {
    if (this.state.favorites.includes(id)) {
      this.state.favorites = this.state.favorites.filter((item) => item !== id);
    } else {
      this.state.favorites = [...this.state.favorites, id];
    }
    saveState(this.state);
    await this.request(true);
    return this.state.favorites.includes(id);
  }

  async getFavorites(): Promise<Listing[]> {
    const listings = seedListings
      .filter((item) => this.state.favorites.includes(item.id))
      .map((item) => ({ ...item, favorite: true }));
    return this.request(listings);
  }

  async getSellerProfile(sellerId: string): Promise<SellerProfile> {
    const listing = seedListings.find((item) => item.sellerId === sellerId) ?? seedListings[0];
    const reviews: SellerReview[] = [];
    return this.request({
      seller: { id: sellerId, displayName: listing.seller, avatarUrl: null, city: listing.city, memberSince: "2024-01" },
      counts: { activeAds: seedListings.filter((item) => item.seller === listing.seller).length, reviews: 0 },
      reputation: { average: listing.sellerRating, reviewersCount: listing.sellerRating ? 1 : 0, reviewsCount: 0, distribution: {} },
      reviews,
    });
  }

  async getSellerReviews(_sellerId: string, _rating?: number, _cursor?: string): Promise<{ items: SellerReview[]; nextCursor: string | null; hasMore: boolean }> {
    return this.request({ items: [], nextCursor: null, hasMore: false });
  }

  async getReviewOpportunities(_sellerId: string, _cursor?: string): Promise<{ items: []; nextCursor: null; hasMore: false }> {
    return this.request({ items: [], nextCursor: null, hasMore: false });
  }

  async getSellerReviewManage(reviewId: string): Promise<{ reviewId: string; version: number; status: string; editableUntil: null; actions: { canEdit: false; canHide: false; canReply: false; canEditReply: false; canReport: false } }> {
    return this.request({ reviewId, version: 1, status: "approved", editableUntil: null, actions: { canEdit: false, canHide: false, canReply: false, canEditReply: false, canReport: false } });
  }

  async getModerationQueue(): Promise<[]> {
    return this.request([]);
  }

  async moderateSellerReview(_reviewId: string, _action: "approve" | "reject" | "hide"): Promise<void> {
    return this.request(undefined);
  }

  async createSellerReview(_sellerId: string, _input: { adId: string; conversationId: string; rating: number; text: string; outcome: string }): Promise<{ id: string; status: string }> {
    return this.request({ id: `review-${Date.now()}`, status: "pending" });
  }

  async updateSellerReview(_reviewId: string, _input: { version: number; rating: number; text: string; outcome: string }): Promise<{ version: number; status: string }> {
    return this.request({ version: 2, status: "pending" });
  }

  async hideSellerReview(_reviewId: string): Promise<void> {
    return this.request(undefined);
  }

  async replySellerReview(_reviewId: string, _text: string): Promise<void> {
    return this.request(undefined);
  }

  async reportSellerReview(_reviewId: string, _reason: string, _details = ""): Promise<void> {
    return this.request(undefined);
  }

  async getAdComments(adId: string): Promise<AdComment[]> {
    return this.request([]);
  }

  async addAdComment(_adId: string, text: string): Promise<import("./types").AdComment> {
    return this.request({ id: `comment-${Date.now()}`, author: "Вы", text, time: "сейчас" });
  }

  async getConversations(): Promise<Conversation[]> {
    const messages = this.state.messages["chat-1"] ?? [
      { id: "m-1", author: "other" as const, text: "Здравствуйте! Подскажите, кресло ещё доступно?", time: "10:44" },
      { id: "m-2", author: "me" as const, text: "Да, можно посмотреть сегодня после 18:00.", time: "10:46" },
    ];
    const result: Conversation[] = [
      {
        id: "chat-1",
        name: "Алина",
        listing: "Кресло, которое ждёт новый дом",
        preview: messages[messages.length - 1]?.text ?? "",
        time: messages[messages.length - 1]?.time ?? "10:46",
        unread: 1,
        messages,
      },
      {
        id: "chat-2",
        name: "Михаил",
        listing: "Плёночная камера",
        preview: "Спасибо, договорились",
        time: "вчера",
        unread: 0,
        messages: [],
      },
    ];
    const supportMessages = this.state.messages.support ?? [];
    const support: Conversation = {
      id: "support",
      name: "Поддержка",
      listing: "Поддержка / вопросы / помощь",
      preview: supportMessages.at(-1)?.text ?? "Напишите о баге или вопросе",
      time: supportMessages.at(-1)?.time ?? "всегда",
      unread: 0,
      isSupport: true,
      messages: supportMessages,
    };
    return this.request([...result, support]);
  }

  async getInbox(): Promise<ChatInbox> {
    const conversations = await this.getConversations();
    const items: ChatInboxItem[] = conversations
      .filter((conversation) => conversation.unread > 0)
      .map((conversation, index) => ({
        id: index + 1,
        kind: "message",
        conversationId: conversation.id,
        listingTitle: conversation.listing,
        messageSequence: conversation.messages.length,
        createdAt: new Date().toISOString(),
        readAt: null,
      }));
    return this.request({ items, unreadCount: items.length });
  }

  async setTelegramWriteAccess(granted: boolean): Promise<boolean> {
    return this.request(granted);
  }

  async blockConversation(_chatId: string): Promise<boolean> {
    return this.request(true);
  }

  async unblockConversation(_chatId: string): Promise<boolean> {
    return this.request(false);
  }

  async markConversationSold(chatId: string): Promise<Conversation> {
    const conversations = await this.getConversations();
    const current = conversations.find((item) => item.id === chatId);
    if (!current) throw new Error("conversation_not_found");
    const updated = { ...current, dealStatus: "sold" as const, canMarkSold: false };
    return this.request(updated);
  }

  async rateAd(_adId: string, _conversationId: string, _rating: number, _text = ""): Promise<void> {
    return this.request(undefined);
  }

  async reportMessage(
    _chatId: string,
    _messageId: string,
    _reason: "spam" | "fraud" | "abuse" | "other",
  ): Promise<void> {
    return this.request(undefined);
  }

  async startConversation(listingId: string): Promise<Conversation> {
    const conversations = await this.getConversations();
    const existing = conversations.find((item) => item.id === `chat-${listingId.replace("ad-", "")}`);
    if (existing) return existing;
    const listing = seedListings.find((item) => item.id === listingId);
    const created: Conversation = {
      id: `chat-${listingId}`,
      name: listing?.seller ?? "Продавец",
      listing: listing?.title ?? "Объявление",
      preview: "Диалог начат",
      time: "сейчас",
      unread: 0,
      messages: [],
    };
    return this.request(created);
  }

  async getConversationMessages(chatId: string, _afterSequence?: number): Promise<ChatMessage[]> {
    const conversations = await this.getConversations();
    return conversations.find((item) => item.id === chatId)?.messages ?? [];
  }

  async markConversationRead(_chatId: string, lastReadSeq: number): Promise<number> {
    return this.request(lastReadSeq);
  }

  subscribeToConversation(
    _chatId: string,
    _listener: ConversationEventListener,
    _onReconnect?: (afterSequence: number) => void,
    _initialSequence?: number,
  ): () => void {
    return () => undefined;
  }

  async sendMessage(
    chatId: string,
    text: string,
    files: File[] = [],
    _clientMessageId?: string,
  ): Promise<ChatMessage> {
    if (!text.trim() && !files.length) throw new Error("message_invalid");
    if (files.length > 4) throw new Error("attachment_invalid");
    const attachments: ChatAttachment[] = files.map((file) => ({
      id: crypto.randomUUID(),
      contentType: file.type,
      sizeBytes: file.size,
      width: 0,
      height: 0,
      url: URL.createObjectURL(file),
    }));
    if (attachments.length) {
      const urls = this.privateAttachmentUrls.get(chatId) ?? [];
      this.privateAttachmentUrls.set(chatId, [...urls, ...attachments.map((item) => item.url)]);
    }
    const message: ChatMessage = {
      id: crypto.randomUUID(),
      author: "me",
      text,
      time: formatNow(),
      attachments,
    };
    this.state.messages[chatId] = [...(this.state.messages[chatId] ?? []), message];
    saveState(this.state);
    return this.request(message);
  }

  releaseConversationMedia(chatId: string): void {
    (this.privateAttachmentUrls.get(chatId) ?? []).forEach((url) => URL.revokeObjectURL(url));
    this.privateAttachmentUrls.delete(chatId);
  }

  getDraft(): Draft {
    return { ...this.state.draft };
  }

  async saveDraft(patch: Partial<Draft>): Promise<Draft> {
    this.state.draft = { ...this.state.draft, ...patch };
    saveState(this.state);
    return this.request({ ...this.state.draft });
  }

  async submitDraft(draft: Draft, _files: File[]): Promise<void> {
    this.state.draft = { ...draft };
    this.state.submitted = true;
    saveState(this.state);
    await this.request(undefined);
  }

  get submitted(): boolean {
    return this.state.submitted;
  }
}

function formatNow(): string {
  return new Intl.DateTimeFormat("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(new Date());
}
